import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Check, ChevronRight, Circle } from "lucide-react";

import { cn } from "@/lib/utils"
import { usePhone } from "@/hooks/use-media-query"

/**
 * `modal={false}` under a pointer, and the page depends on it.
 *
 * Radix defaults a dropdown to modal, which locks body scroll while the menu
 * is open — and locking scroll REMOVES the scrollbar. On a long page that
 * always has one, opening a two-item menu made the document 15px wider,
 * shoved every centred thing sideways, and put it back on close. Nothing
 * here needs modality: outside clicks still dismiss it, arrow keys still move
 * within it. On a phone the menu is a bottom sheet over a dimmed page and
 * SHOULD lock the scroll behind it, so modality follows the shape.
 */
const DropdownMenu = (props: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) => {
  const phone = usePhone()
  return <DropdownMenuPrimitive.Root modal={phone} {...props} />
}

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

/** A menu row. 44px under a finger; 52px in the phone sheet (set by the content). */
const ITEM =
  "relative flex cursor-default select-none items-center gap-2.5 rounded-sm px-2.5 py-2 text-label font-semibold text-foreground outline-hidden " +
  "transition-colors duration-instant ease-standard data-[highlighted]:bg-foreground/[.06] " +
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch:min-h-touch"

/**
 * Re-asserted AFTER the caller's className on a phone. Nineteen call sites
 * pass a desktop width (`w-56`, `w-[400px]`, `w-full`) and a few pass a
 * radius; on a phone the content is a full-width bottom sheet and none of
 * those may survive. The account menu once passed `w-[calc(100vw-2rem)]
 * max-w-md !rounded-xl` and rendered as a floating card 33px short of the
 * right edge, moving with the popper's re-measurements. tailwind-merge keeps
 * the last class in a group, so putting these last is what makes the
 * primitive's decision final.
 */
const SHEET_LOCK =
  "w-full max-w-none min-w-0 rounded-t-xl rounded-b-none border-x-0 border-b-0 max-h-[calc(var(--vv-h,100dvh)_*_0.8)]"

/** The panel a menu opens as, under a pointer. */
const PANEL =
  "z-popover min-w-[14rem] overflow-y-auto overflow-x-hidden rounded-md p-[5px] text-foreground surface-elevated " +
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
  "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 " +
  "duration-panel ease-standard"

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(ITEM, "data-[state=open]:bg-foreground/[.06]", inset && "pl-8", className)}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(PANEL, "max-h-[300px] origin-(--radix-dropdown-menu-content-transform-origin)", className)}
    {...props}
  />
))
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

/**
 * The menu's two shapes. Under a pointer it is the anchored panel Radix
 * positions. On a phone it is a bottom sheet — full width, 52px rows a thumb
 * can land on, a rule between groups, the page dimmed behind it — because a
 * 224px card anchored to a ⋯ button on a 390pt screen puts the card's most
 * irreversible action in a 36pt row a thumb can brush on the way past.
 *
 * The mechanism is the same as ui/popover.tsx: `data-phone-sheet` on the
 * content, and index.css re-pins Radix's popper wrapper to the bottom edge.
 * The scrim is the content's own spread shadow. Items inside are restyled
 * from here (`[&_[role^=menuitem]]`) so every existing DropdownMenuItem gets
 * the row treatment with no call-site edit.
 */
const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 6, onCloseAutoFocus, ...props }, ref) => {
  const phone = usePhone()
  /* Focus goes back to the trigger WITHOUT scrolling, as ui/dialog's does:
     Radix's own return scrolled the page whenever the trigger sat under the
     sticky header, so closing a menu moved the page (shift-audit
     scroll-jump, Sales list's More, 2026-11-02). The trigger is the control
     whose aria-controls names this menu, read as the menu mounts (Radix
     drops the attribute once it closes). */
  const trigger = React.useRef<HTMLElement | null>(null)
  const setRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node?.id) trigger.current = document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(node.id)}"]`)
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={setRef}
        onCloseAutoFocus={(e) => {
          onCloseAutoFocus?.(e)
          if (e.defaultPrevented) return
          const target = trigger.current
          if (target?.isConnected) {
            e.preventDefault()
            target.focus({ preventScroll: true })
          }
        }}
        sideOffset={sideOffset}
        data-phone-sheet={phone ? "" : undefined}
        className={cn(
          phone
            ? cn(
                "z-popover w-full max-w-none overflow-y-auto scroll-pane text-foreground surface-elevated",
                "rounded-t-xl rounded-b-none border-x-0 border-b-0",
                "px-3 pt-3 pb-[calc(0.75rem+var(--sa-bottom))]",
                "shadow-[0_0_0_100vmax_hsl(var(--scrim)/0.55)]",
                "data-[state=open]:dialog-sheet-in data-[state=closed]:dialog-sheet-out",
                // Rows: 52px, body size, full width, a hairline between them.
                "[&_[role^=menuitem]]:min-h-[52px] [&_[role^=menuitem]]:rounded [&_[role^=menuitem]]:px-4 [&_[role^=menuitem]]:text-body",
                // A radio or checkbox row keeps room for its indicator, which
                // `px-4` above would otherwise slide the label under. The doubled
                // attribute outranks that rule whatever order the CSS lands in.
                "[&_[role=menuitemradio][role]]:pl-11 [&_[role=menuitemcheckbox][role]]:pl-11 [&_[role^=menuitem][role]>span:first-child]:left-4",
                "[&_[role=separator]]:my-2 [&_[role=separator]]:mx-1",
                className,
                SHEET_LOCK
              )
            : cn(PANEL, "max-h-[var(--radix-dropdown-menu-content-available-height)] origin-(--radix-dropdown-menu-content-transform-origin)", className)
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(ITEM, inset && "pl-8", className)}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(ITEM, "pl-8", className)}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(ITEM, "pl-8", className)}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn("px-2.5 py-1.5 type-eyebrow text-muted-foreground", inset && "pl-8", className)}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("mx-2 my-1 h-px bg-border/70", className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-caption tracking-widest opacity-60", className)}
      {...props}
    />
  )
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
